import { useCallback, useRef, useState, useLayoutEffect } from 'react';

type UseChatResizeProps = {
  width: number;
  height: number;
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
};

export function useChatResize({
  width,
  height,
  minWidth,
  maxWidth,
  minHeight,
  maxHeight
}: UseChatResizeProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  const [currentWidth, setCurrentWidth] = useState(width);
  const [currentHeight, setCurrentHeight] = useState(height);

  const currentWidthRef = useRef(currentWidth);

  const currentHeightRef = useRef(currentHeight);

  useLayoutEffect(() => {
    currentWidthRef.current = currentWidth;
  }, [currentWidth]);

  useLayoutEffect(() => {
    currentHeightRef.current = currentHeight;
  }, [currentHeight]);

  useLayoutEffect(() => {
    if (width !== currentWidthRef.current) {
      setCurrentWidth(width);
    }
  }, [width]);

  useLayoutEffect(() => {
    if (height !== currentHeightRef.current) {
      setCurrentHeight(height);
    }
  }, [height]);

  const resizeStateRef = useRef<{
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!resizeStateRef.current) return;

      const {
        startX,
        startY,
        startWidth,
        startHeight
      } = resizeStateRef.current;

      // TOP-LEFT handle math
      const deltaX = startX - e.clientX;
      const deltaY = startY - e.clientY;

      const nextWidth = Math.min(
        Math.max(startWidth + deltaX, minWidth),
        maxWidth
      );

      const nextHeight = Math.min(
        Math.max(startHeight + deltaY, minHeight),
        maxHeight
      );

      setCurrentWidth(nextWidth);
      setCurrentHeight(nextHeight);
    },
    [minWidth, maxWidth, minHeight, maxHeight]
  );

  const onMouseUp = useCallback(() => {
    resizeStateRef.current = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('blur', onMouseUp);

    // ✅ restore browser behaviour
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, [onMouseMove]);

  const handleResize = useCallback(
    (e: React.MouseEvent) => {
      if (!ref.current) return;
      // ✅ prevents text selection start
      e.preventDefault();

      const rect = ref.current.getBoundingClientRect();

      resizeStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startWidth: rect.width,
        startHeight: rect.height
      };

      // ✅ block text selection + lock cursor
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'nwse-resize';

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      window.addEventListener('blur', onMouseUp);
    },
    [onMouseMove, onMouseUp]
  );

  return {
    ref,
    width: currentWidth,
    height: currentHeight,
    handleResize
  };
}
